import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CheckCircle, FileText, Search, CreditCard, Calendar, Trash2, ShoppingCart, Truck, Package, Tag, Percent, Printer, Edit2, Sliders, Pencil, Check, X, ArrowUpDown, RotateCcw, Copy } from 'lucide-react';
import Modal from '../components/Modal';
import SearchableSelect from '../components/SearchableSelect';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function SalesOrders() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pedido'); // 'pedido' | 'transporte'
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [priceTables, setPriceTables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recente');

  const [formData, setFormData] = useState({
    id: null,
    venda_id: '',
    cliente_id: '',
    cliente_nome: '',
    tabela_id: '',
    tabela_nome: '',
    desconto_porcentagem: 0,
    desconto: 0,
    data_contabilizacao: new Date().toISOString().split('T')[0],
    valor_total: '0.00',
    observacoes: '',
    status_pagamento: 'PENDENTE',
    itens: [],
    // Dados de Transporte
    transportadora_id: '',
    transportadora_nome: '',
    mod_frete: 9,
    placa_veiculo: '',
    uf_veiculo: 'SP',
    rntrc: '',
    q_vol: 0,
    esp: '',
    marca: '',
    peso_b: 0,
    peso_l: 0
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);

  const calculateOrderTotal = (itens, descontoVal) => {
    const subtotalItens = (itens || []).reduce((sum, item) => sum + (Number(item.subtotal) || 0), 0);
    const rawDesc = parseFloat(String(descontoVal ?? 0).replace(',', '.'));
    const descPerc = isNaN(rawDesc) ? 0 : Math.min(100, Math.max(0, rawDesc));
    const finalTotal = subtotalItens * (1 - (descPerc / 100));
    return Math.max(0, finalTotal).toFixed(2);
  };

  // Estado para a Modal de Características do Item
  const [isCharModalOpen, setIsCharModalOpen] = useState(false);
  const [currentCharItemIndex, setCurrentCharItemIndex] = useState(null);
  const [charFormData, setCharFormData] = useState({
    cor: '',
    caimento: '',
    vareta: '',
    extras: []
  });

  const openCharModal = (index) => {
    const item = formData.itens[index];
    if (!item) return;

    let chars = { cor: '', caimento: '', vareta: '', extras: [] };
    if (item.caracteristicas) {
      if (typeof item.caracteristicas === 'string') {
        try {
          chars = JSON.parse(item.caracteristicas);
        } catch (e) {
          chars = { cor: item.caracteristicas, caimento: '', vareta: '', extras: [] };
        }
      } else if (typeof item.caracteristicas === 'object') {
        chars = { ...item.caracteristicas };
      }
    }

    if (!Array.isArray(chars.extras)) {
      chars.extras = [];
    }

    setCharFormData({
      cor: chars.cor || '',
      caimento: chars.caimento || '',
      vareta: chars.vareta || '',
      extras: chars.extras ? chars.extras.map(e => ({ titulo: e.titulo || '', detalhe: e.detalhe || '' })) : []
    });
    setCurrentCharItemIndex(index);
    setIsCharModalOpen(true);
  };

  const handleCharInputChange = (e) => {
    const { name, value } = e.target;
    setCharFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddExtraChar = () => {
    setCharFormData(prev => ({
      ...prev,
      extras: [...prev.extras, { titulo: '', detalhe: '' }]
    }));
  };

  const handleExtraCharChange = (index, field, value) => {
    setCharFormData(prev => {
      const updatedExtras = [...prev.extras];
      updatedExtras[index] = { ...updatedExtras[index], [field]: value };
      return { ...prev, extras: updatedExtras };
    });
  };

  const handleRemoveExtraChar = (index) => {
    setCharFormData(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  const handleSaveCharacteristics = () => {
    if (currentCharItemIndex === null) return;

    const cleanedExtras = charFormData.extras.filter(e => (e.titulo && e.titulo.trim() !== '') || (e.detalhe && e.detalhe.trim() !== ''));
    const updatedChars = {
      cor: charFormData.cor,
      caimento: charFormData.caimento,
      vareta: charFormData.vareta,
      extras: cleanedExtras
    };

    setFormData(prev => {
      const updatedItens = [...prev.itens];
      updatedItens[currentCharItemIndex] = {
        ...updatedItens[currentCharItemIndex],
        caracteristicas: updatedChars
      };
      return { ...prev, itens: updatedItens };
    });

    setIsCharModalOpen(false);
    setCurrentCharItemIndex(null);
  };

  // Estado para Edição Inline do Produto do Item
  const [editingProductIndex, setEditingProductIndex] = useState(null);
  const [tempProductValue, setTempProductValue] = useState('');

  const startEditingProduct = (index, currentProductId) => {
    setEditingProductIndex(index);
    setTempProductValue(currentProductId ? String(currentProductId) : '');
    setEditingQtyIndex(null);
    setEditingPriceIndex(null);
  };

  const cancelEditingProduct = () => {
    setEditingProductIndex(null);
    setTempProductValue('');
  };

  const saveEditingProduct = (index, forcedProductId = null) => {
    const prodIdToSave = forcedProductId !== null ? forcedProductId : tempProductValue;
    if (!prodIdToSave) {
      showAlert('Aviso', 'Selecione um produto.');
      return;
    }

    const selectedProd = products.find(p => String(p.id) === String(prodIdToSave));
    if (!selectedProd) {
      showAlert('Aviso', 'Produto selecionado não encontrado.');
      return;
    }

    setFormData(prev => {
      const updatedItens = [...prev.itens];
      const currentItem = updatedItens[index];
      const qty = Number(currentItem.quantidade || 1);

      const rawPrice = selectedProd.produto_preco !== undefined && selectedProd.produto_preco !== null
        ? String(selectedProd.produto_preco).replace(',', '.')
        : '0';
      const basePrice = parseFloat(rawPrice) || 0;
      const descPerc = parseFloat(prev.desconto_porcentagem || 0);
      const discountedPrice = basePrice * (1 - (descPerc / 100));
      const newSubtotal = discountedPrice * qty;

      updatedItens[index] = {
        ...currentItem,
        produto_id: selectedProd.id,
        produto_nome: selectedProd.produto_nome,
        preco_original: basePrice,
        preco_unitario: discountedPrice,
        subtotal: newSubtotal
      };

      return {
        ...prev,
        itens: updatedItens,
        valor_total: calculateOrderTotal(updatedItens, prev.desconto)
      };
    });

    setEditingProductIndex(null);
    setTempProductValue('');
  };

  // Estado para Edição Inline da Quantidade do Item
  const [editingQtyIndex, setEditingQtyIndex] = useState(null);
  const [tempQtyValue, setTempQtyValue] = useState('');

  const startEditingQty = (index, currentQty) => {
    setEditingQtyIndex(index);
    setTempQtyValue(currentQty.toString());
    setEditingProductIndex(null);
    setEditingPriceIndex(null);
  };

  const cancelEditingQty = () => {
    setEditingQtyIndex(null);
    setTempQtyValue('');
  };

  const saveEditingQty = (index) => {
    const newQty = parseInt(tempQtyValue, 10);
    if (isNaN(newQty) || newQty <= 0) {
      showAlert('Aviso', 'Informe uma quantidade válida maior que zero.');
      return;
    }

    setFormData(prev => {
      const updatedItens = [...prev.itens];
      const currentItem = updatedItens[index];
      const pUnit = currentItem.preco_unitario || 0;
      const newSubtotal = pUnit * newQty;

      updatedItens[index] = {
        ...currentItem,
        quantidade: newQty,
        subtotal: newSubtotal
      };

      return {
        ...prev,
        itens: updatedItens,
        valor_total: calculateOrderTotal(updatedItens, prev.desconto)
      };
    });

    setEditingQtyIndex(null);
    setTempQtyValue('');
  };

  // Estado para Edição Inline do Valor Unitário do Item
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  const [tempPriceValue, setTempPriceValue] = useState('');

  const startEditingPrice = (index, currentPrice) => {
    setEditingPriceIndex(index);
    setTempPriceValue(Number(currentPrice || 0).toFixed(2));
    setEditingProductIndex(null);
    setEditingQtyIndex(null);
  };

  const cancelEditingPrice = () => {
    setEditingPriceIndex(null);
    setTempPriceValue('');
  };

  const saveEditingPrice = async (index) => {
    const newPrice = parseFloat(tempPriceValue.toString().replace(',', '.'));
    if (isNaN(newPrice) || newPrice < 0) {
      showAlert('Aviso', 'Informe um valor unitário válido.');
      return;
    }

    const currentItem = formData.itens[index];
    const productId = currentItem ? currentItem.produto_id : null;

    setFormData(prev => {
      const updatedItens = [...prev.itens];
      const item = updatedItens[index];
      const qty = item.quantidade || 0;
      const newSubtotal = newPrice * qty;

      updatedItens[index] = {
        ...item,
        preco_unitario: newPrice,
        preco_original: newPrice,
        subtotal: newSubtotal
      };

      return {
        ...prev,
        itens: updatedItens,
        valor_total: calculateOrderTotal(updatedItens, prev.desconto)
      };
    });

    if (productId) {
      try {
        const prod = products.find(p => String(p.id) === String(productId));
        if (prod) {
          const updatedProd = { ...prod, produto_preco: newPrice };
          await fetch(`${API_BASE_URL}/produtos.php`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatedProd)
          });
          fetchProducts();
        }
      } catch (err) {
        console.error('Erro ao atualizar preço do produto no estoque:', err);
      }
    }

    setEditingPriceIndex(null);
    setTempPriceValue('');
  };

  useEffect(() => {
    fetchOrders();
    fetchClients();
    fetchProducts();
    fetchCarriers();
    fetchPriceTables();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setOrders(data);
      }
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setClients(data);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/produtos.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setProducts(data);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/transportadoras.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setCarriers(data);
      }
    } catch (error) {
      console.error('Erro ao buscar transportadoras:', error);
    }
  };

  const fetchPriceTables = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tabelas.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setPriceTables(data);
      }
    } catch (error) {
      console.error('Erro ao buscar tabelas de preço:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDiscountChange = (e) => {
    let val = e.target.value;

    if (val === '') {
      setFormData(prev => ({
        ...prev,
        desconto: '',
        valor_total: calculateOrderTotal(prev.itens, 0)
      }));
      return;
    }

    val = val.replace(',', '.');

    // Aceitar somente números com no máximo 2 casas decimais
    if (!/^\d*(\.\d{0,2})?$/.test(val)) {
      return;
    }

    let numVal = parseFloat(val);
    if (!isNaN(numVal)) {
      if (numVal < 0) {
        numVal = 0;
        val = '0';
      }
      if (numVal > 100) {
        numVal = 100;
        val = '100';
      }
    }

    setFormData(prev => ({
      ...prev,
      desconto: val,
      valor_total: calculateOrderTotal(prev.itens, numVal || 0)
    }));
  };

  const handleTableChange = (e) => {
    const tableId = e.target.value;
    const table = priceTables.find(t => String(t.id) === String(tableId));

    const newTabelaId = tableId;
    const newTabelaNome = table ? table.nome : '';
    const newDescontoPerc = table ? parseFloat(table.desconto_porcentagem || 0) : 0;

    setFormData(prev => {
      const updatedItens = prev.itens.map(item => {
        const basePrice = item.preco_original !== undefined ? item.preco_original : item.preco_unitario;
        const discountedPrice = basePrice * (1 - (newDescontoPerc / 100));
        return {
          ...item,
          preco_original: basePrice,
          preco_unitario: discountedPrice,
          subtotal: discountedPrice * item.quantidade
        };
      });

      return {
        ...prev,
        tabela_id: newTabelaId,
        tabela_nome: newTabelaNome,
        desconto_porcentagem: newDescontoPerc,
        itens: updatedItens,
        valor_total: calculateOrderTotal(updatedItens, prev.desconto)
      };
    });
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    const client = clients.find(c => c.cliente_id === clientId || c.id.toString() === clientId);
    
    let transpId = '';
    let transpNome = '';

    if (client && client.transportadora_id) {
      transpId = client.transportadora_id;
      const carrier = carriers.find(tr => String(tr.id) === String(client.transportadora_id));
      if (carrier) {
        transpNome = carrier.nome;
      }
    }

    setFormData(prev => ({
      ...prev,
      cliente_id: clientId,
      cliente_nome: client ? client.cliente_nome : '',
      transportadora_id: transpId,
      transportadora_nome: transpNome
    }));
  };

  const handleCarrierSelectChange = (e) => {
    const carrierId = e.target.value;
    const carrier = carriers.find(c => String(c.id) === String(carrierId));
    setFormData(prev => ({
      ...prev,
      transportadora_id: carrierId,
      transportadora_nome: carrier ? carrier.nome : ''
    }));
  };

  const handleAddItem = () => {
    if (!selectedProduct || itemQuantity <= 0) return;

    const product = products.find(p => p.id.toString() === selectedProduct);
    if (!product) return;

    const basePrice = parseFloat(product.produto_preco) || 0;
    const descPerc = parseFloat(formData.desconto_porcentagem) || 0;
    const discountedPrice = basePrice * (1 - (descPerc / 100));
    const qty = parseInt(itemQuantity, 10);

    const newItem = {
      produto_id: product.id,
      produto_nome: product.produto_nome,
      quantidade: qty,
      preco_original: basePrice,
      preco_unitario: discountedPrice,
      subtotal: discountedPrice * qty,
      caracteristicas: { cor: '', caimento: '', vareta: '', extras: [] }
    };

    setFormData(prev => {
      const updatedItens = [...prev.itens, newItem];
      return {
        ...prev,
        itens: updatedItens,
        valor_total: calculateOrderTotal(updatedItens, prev.desconto)
      };
    });

    setSelectedProduct('');
    setItemQuantity(1);
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => {
      const updatedItens = prev.itens.filter((_, i) => i !== index);
      return {
        ...prev,
        itens: updatedItens,
        valor_total: calculateOrderTotal(updatedItens, prev.desconto)
      };
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.cliente_id) {
      showAlert('Aviso', 'Selecione o Cliente do pedido.');
      return;
    }
    if (formData.itens.length === 0) {
      showAlert('Aviso', 'Adicione ao menos um produto no pedido.');
      return;
    }

    const method = formData.id ? 'PUT' : 'POST';
    const payload = { ...formData };
    if (!payload.id && !payload.venda_id) {
      payload.venda_id = 'PED' + Math.floor(Math.random() * 100000);
    }
    const rawDesc = parseFloat(String(payload.desconto ?? 0).replace(',', '.'));
    payload.desconto = isNaN(rawDesc) ? 0 : Math.min(100, Math.max(0, Number(rawDesc.toFixed(2))));
    payload.valor_total = parseFloat(calculateOrderTotal(payload.itens, payload.desconto)) || 0;

    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchOrders();
        fetchProducts();
        showAlert('Sucesso', 'Pedido de Venda salvo com sucesso!');
      } else {
        showAlert('Aviso', 'Erro ao salvar pedido: ' + (result.error || 'Desconhecido'));
      }
    } catch (error) {
      console.error('Erro:', error);
      showAlert('Erro', 'Falha na comunicação com o servidor');
    }
  };

  const handleTogglePayment = async (order) => {
    const isPaid = order.status_pagamento === 'PAGO';
    const newStatus = isPaid ? 'PENDENTE' : 'PAGO';
    const confirmTitle = isPaid ? 'Reverter Pagamento' : 'Confirmar Pagamento';
    const confirmMsg = isPaid 
      ? `Deseja reverter o pagamento do pedido #${order.venda_id || order.id} para PENDENTE?`
      : `Deseja marcar o pedido #${order.venda_id || order.id} como PAGO?`;

    const confirmed = await showConfirm(confirmTitle, confirmMsg);
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...order, status_pagamento: newStatus })
      });
      const result = await response.json();
      if (result.success) {
        fetchOrders();
        showAlert('Sucesso', isPaid ? 'Pagamento revertido para PENDENTE com sucesso!' : 'Pedido marcado como PAGO com sucesso!');
      } else {
        showAlert('Aviso', result.error || 'Erro ao alterar status de pagamento.');
      }
    } catch (error) {
      console.error('Erro:', error);
      showAlert('Erro', 'Falha na comunicação com o servidor.');
    }
  };

  const handleGenerateInvoice = (id) => {
    navigate(`/pedidos/${id}/nota-fiscal`);
  };

  const handleEditOrder = (order) => {
    if (order.status_pagamento === 'PAGO') {
      showAlert('Aviso', 'Pedidos de venda pagos não podem ser editados.');
      return;
    }

    setFormData({ 
      id: order.id, 
      venda_id: order.venda_id || '', 
      cliente_id: order.cliente_id || '', 
      cliente_nome: order.cliente_nome || '', 
      tabela_id: order.tabela_id || '',
      tabela_nome: order.tabela_nome || '',
      desconto_porcentagem: order.desconto_porcentagem || 0,
      desconto: order.desconto !== undefined && order.desconto !== null ? Number(order.desconto) : 0,
      data_contabilizacao: order.data_contabilizacao || new Date().toISOString().split('T')[0], 
      valor_total: order.valor_total || '0.00', 
      observacoes: order.observacoes || '', 
      status_pagamento: order.status_pagamento || 'PENDENTE', 
      itens: order.itens ? order.itens.map(i => {
        const pOrig = i.preco_original !== undefined && i.preco_original !== null ? Number(i.preco_original) : Number(i.preco_unitario || 0);
        const pUnit = Number(i.preco_unitario || 0);
        const qty = Number(i.quantidade || 0);
        const sub = i.subtotal !== undefined && i.subtotal !== null ? Number(i.subtotal) : (pUnit * qty);
        
        let chars = { cor: '', caimento: '', vareta: '', extras: [] };
        if (i.caracteristicas) {
          if (typeof i.caracteristicas === 'string') {
            try { chars = JSON.parse(i.caracteristicas); } catch(e) {}
          } else if (typeof i.caracteristicas === 'object') {
            chars = i.caracteristicas;
          }
        }

        return {
          ...i,
          quantidade: qty,
          preco_original: pOrig,
          preco_unitario: pUnit,
          subtotal: sub,
          caracteristicas: chars
        };
      }) : [],
      transportadora_id: order.transportadora_id || '',
      transportadora_nome: order.transportadora_nome || '',
      mod_frete: order.mod_frete !== undefined ? order.mod_frete : 9,
      placa_veiculo: order.placa_veiculo || '',
      uf_veiculo: order.uf_veiculo || 'SP',
      rntrc: order.rntrc || '',
      q_vol: order.q_vol || 0,
      esp: order.esp || '',
      marca: order.marca || '',
      peso_b: order.peso_b || 0,
      peso_l: order.peso_l || 0
    });
    setSelectedProduct('');
    setItemQuantity(1);
    setEditingProductIndex(null);
    setTempProductValue('');
    setEditingQtyIndex(null);
    setTempQtyValue('');
    setEditingPriceIndex(null);
    setTempPriceValue('');
    setActiveTab('pedido');
    setIsModalOpen(true);
  };

  const handleDuplicateOrder = (order) => {
    setFormData({ 
      id: null, 
      venda_id: '', 
      cliente_id: order.cliente_id || '', 
      cliente_nome: order.cliente_nome || '', 
      tabela_id: order.tabela_id || '',
      tabela_nome: order.tabela_nome || '',
      desconto_porcentagem: order.desconto_porcentagem || 0,
      desconto: order.desconto !== undefined && order.desconto !== null ? Number(order.desconto) : 0,
      data_contabilizacao: new Date().toISOString().split('T')[0], 
      valor_total: order.valor_total || '0.00', 
      observacoes: order.observacoes ? `[Cópia do Pedido #${order.venda_id || order.id}] ${order.observacoes}` : `[Cópia do Pedido #${order.venda_id || order.id}]`, 
      status_pagamento: 'PENDENTE', 
      itens: order.itens ? order.itens.map(i => {
        const pOrig = i.preco_original !== undefined && i.preco_original !== null ? Number(i.preco_original) : Number(i.preco_unitario || 0);
        const pUnit = Number(i.preco_unitario || 0);
        const qty = Number(i.quantidade || 0);
        const sub = i.subtotal !== undefined && i.subtotal !== null ? Number(i.subtotal) : (pUnit * qty);
        
        let chars = { cor: '', caimento: '', vareta: '', extras: [] };
        if (i.caracteristicas) {
          if (typeof i.caracteristicas === 'string') {
            try { chars = JSON.parse(i.caracteristicas); } catch(e) {}
          } else if (typeof i.caracteristicas === 'object') {
            chars = i.caracteristicas;
          }
        }

        return {
          ...i,
          id: undefined,
          quantidade: qty,
          preco_original: pOrig,
          preco_unitario: pUnit,
          subtotal: sub,
          caracteristicas: chars
        };
      }) : [],
      transportadora_id: order.transportadora_id || '',
      transportadora_nome: order.transportadora_nome || '',
      mod_frete: order.mod_frete !== undefined ? order.mod_frete : 9,
      placa_veiculo: order.placa_veiculo || '',
      uf_veiculo: order.uf_veiculo || 'SP',
      rntrc: order.rntrc || '',
      q_vol: order.q_vol || 0,
      esp: order.esp || '',
      marca: order.marca || '',
      peso_b: order.peso_b || 0,
      peso_l: order.peso_l || 0
    });
    setSelectedProduct('');
    setItemQuantity(1);
    setEditingProductIndex(null);
    setTempProductValue('');
    setEditingQtyIndex(null);
    setTempQtyValue('');
    setEditingPriceIndex(null);
    setTempPriceValue('');
    setActiveTab('pedido');
    setIsModalOpen(true);
    showAlert('Pedido Duplicado', `Os dados do pedido #${order.venda_id || order.id} foram duplicados no formulário. Revise os campos e clique em "Salvar Pedido" para registrar.`);
  };

  const openNewModal = () => {
    setFormData({ 
      id: null, 
      venda_id: '', 
      cliente_id: '', 
      cliente_nome: '', 
      tabela_id: '',
      tabela_nome: '',
      desconto_porcentagem: 0,
      desconto: 0,
      data_contabilizacao: new Date().toISOString().split('T')[0], 
      valor_total: '0.00', 
      observacoes: '', 
      status_pagamento: 'PENDENTE', 
      itens: [],
      transportadora_id: '',
      transportadora_nome: '',
      mod_frete: 9,
      placa_veiculo: '',
      uf_veiculo: 'SP',
      rntrc: '',
      q_vol: 0,
      esp: '',
      marca: '',
      peso_b: 0,
      peso_l: 0
    });
    setSelectedProduct('');
    setItemQuantity(1);
    setEditingProductIndex(null);
    setTempProductValue('');
    setEditingQtyIndex(null);
    setTempQtyValue('');
    setEditingPriceIndex(null);
    setTempPriceValue('');
    setActiveTab('pedido');
    setIsModalOpen(true);
  };

  const handleDeleteOrder = async (id) => {
    const order = orders.find(o => o.id === id);
    if (order && order.status_pagamento === 'PAGO') {
      showAlert('Aviso', 'Pedidos de venda pagos não podem ser excluídos.');
      return;
    }

    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja excluir este pedido? Os itens retornarão ao estoque.');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/vendas.php?id=${id}`, { method: 'DELETE' });
      const result = await response.json();
      if (result.success) {
        fetchOrders();
        fetchProducts();
      } else {
        showAlert('Aviso', result.error || 'Erro ao excluir pedido');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const filteredOrders = orders
    .filter(order => 
      (order.cliente_nome && order.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.venda_id && order.venda_id.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.id && order.id.toString().includes(searchTerm)) ||
      (order.tabela_nome && order.tabela_nome.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'recente') {
        const dateA = new Date(a.data_criacao || a.data_contabilizacao || 0).getTime();
        const dateB = new Date(b.data_criacao || b.data_contabilizacao || 0).getTime();
        if (dateB !== dateA) return dateB - dateA;
        return Number(b.id || 0) - Number(a.id || 0);
      }
      if (sortBy === 'antigo') {
        const dateA = new Date(a.data_criacao || a.data_contabilizacao || 0).getTime();
        const dateB = new Date(b.data_criacao || b.data_contabilizacao || 0).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return Number(a.id || 0) - Number(b.id || 0);
      }
      if (sortBy === 'maior_valor') {
        return Number(b.valor_total || 0) - Number(a.valor_total || 0);
      }
      if (sortBy === 'menor_valor') {
        return Number(a.valor_total || 0) - Number(b.valor_total || 0);
      }
      if (sortBy === 'a_z') {
        const nameA = (a.cliente_nome || '').toLowerCase();
        const nameB = (b.cliente_nome || '').toLowerCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      }
      if (sortBy === 'z_a') {
        const nameA = (a.cliente_nome || '').toLowerCase();
        const nameB = (b.cliente_nome || '').toLowerCase();
        return nameB.localeCompare(nameA, 'pt-BR');
      }
      return 0;
    });

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pedidos de Venda</h1>
          <p className="page-subtitle">Acompanhe os pedidos, faturamentos, transporte e emissão de notas fiscais</p>
        </div>
        <button className="btn-primary" onClick={openNewModal} style={{ width: 'auto', padding: '0.65rem 1.25rem' }}>
          <Plus size={18} /> Novo Pedido
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="search-bar" style={{ width: '100%', maxWidth: '380px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar pedido por ID, Cliente ou Tabela..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ArrowUpDown size={16} className="text-muted" />
            <label style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--text-main)', whiteSpace: 'nowrap' }}>
              Ordenar por:
            </label>
            <select
              className="standard-input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ width: 'auto', padding: '0.45rem 2rem 0.45rem 0.75rem', fontSize: '0.8rem' }}
            >
              <option value="recente">Mais recente</option>
              <option value="antigo">Mais antigo</option>
              <option value="maior_valor">Maior valor</option>
              <option value="menor_valor">Menor valor</option>
              <option value="a_z">A-Z</option>
              <option value="z_a">Z-A</option>
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Detalhes / Valor</th>
                <th>Tabela / Frete</th>
                <th>Itens</th>
                <th>Status</th>
                <th style={{ minWidth: '200px', maxWidth: '340px' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td>
                    <div className="font-medium" style={{ color: 'var(--text-main)', fontWeight: '600' }}>#{order.venda_id || order.id}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{order.cliente_nome}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-main)', fontWeight: '600' }}>
                        <CreditCard size={14} className="text-muted" /> R$ {Number(order.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        <Calendar size={14} /> {order.data_contabilizacao}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {order.tabela_nome ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#16a34a', backgroundColor: '#dcfce7', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', width: 'fit-content' }}>
                          <Tag size={12} /> {order.tabela_nome} ({Number(order.desconto_porcentagem || 0).toFixed(1)}% OFF)
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>Tabela: Nenhuma</span>
                      )}
                      {Number(order.desconto || 0) > 0 && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#7c3aed', backgroundColor: '#f3e8ff', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', width: 'fit-content' }}>
                          <Percent size={12} /> Desconto: {Number(order.desconto).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}% OFF
                        </div>
                      )}
                      {order.transportadora_nome ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--primary-color)', fontSize: '0.75rem', fontWeight: '500' }}>
                          <Truck size={12} /> {order.transportadora_nome}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>Sem Frete</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {order.itens && order.itens.length > 0 
                        ? order.itens.map(i => `${i.quantidade}x ${i.produto_nome}`).join(', ') 
                        : 'Sem itens'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${order.status_pagamento === 'PAGO' ? 'badge-success' : 'badge-warning'}`}>
                      {order.status_pagamento || 'PENDENTE'}
                    </span>
                  </td>
                  <td style={{ minWidth: '200px', maxWidth: '340px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                      <button 
                        style={{ 
                          height: '28px', 
                          padding: '0 0.55rem', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          borderRadius: '6px',
                          border: '1px solid #c084fc',
                          backgroundColor: '#f3e8ff',
                          color: '#7c3aed',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.3rem',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease-in-out'
                        }}
                        onClick={() => window.open(`${API_BASE_URL}/orcamento_pdf.php?venda_id=${order.id}`, '_blank')}
                        title="Gerar PDF do Orçamento nº 3515+"
                      >
                        <Printer size={13} /> Orçamento
                      </button>

                      {order.status_pagamento === 'PAGO' ? (
                        <button 
                          style={{ 
                            height: '28px', 
                            padding: '0 0.55rem', 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #f97316',
                            backgroundColor: '#ffedd5',
                            color: '#c2410c',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease-in-out'
                          }}
                          onClick={() => handleTogglePayment(order)}
                          title="Reverter status de pagamento para Pendente"
                        >
                          <RotateCcw size={13} /> Reverter Pagamento
                        </button>
                      ) : (
                        <button 
                          style={{ 
                            height: '28px', 
                            padding: '0 0.55rem', 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #4ade80',
                            backgroundColor: '#dcfce7',
                            color: '#15803d',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease-in-out'
                          }}
                          onClick={() => handleTogglePayment(order)}
                          title="Marcar como Pago"
                        >
                          <CheckCircle size={13} /> Pagar
                        </button>
                      )}

                      <button 
                        style={{ 
                          height: '28px', 
                          padding: '0 0.55rem', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          borderRadius: '6px',
                          border: '1px solid #38bdf8',
                          backgroundColor: '#e0f2fe',
                          color: '#0284c7',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.3rem',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease-in-out'
                        }}
                        onClick={() => handleGenerateInvoice(order.id)}
                        title="Abrir tela de Nota Fiscal do Pedido"
                      >
                        <FileText size={13} /> Nota Fiscal
                      </button>

                      {order.nfe && (
                        <>
                          <button 
                            style={{ 
                              height: '28px', 
                              padding: '0 0.55rem', 
                              fontSize: '0.75rem', 
                              fontWeight: '600',
                              borderRadius: '6px',
                              border: '1px solid #38bdf8',
                              backgroundColor: '#e0f2fe',
                              color: '#0284c7',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.3rem',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease-in-out'
                            }}
                            onClick={() => window.open(`${API_BASE_URL}/danfe_pdf.php?chave=${order.nfe.chave_acesso}`, '_blank')}
                            title="Visualizar DANFE da NF-e"
                          >
                            <FileText size={13} /> Ver NF
                          </button>
                          <button 
                            style={{ 
                              height: '28px', 
                              padding: '0 0.55rem', 
                              fontSize: '0.75rem', 
                              fontWeight: '600',
                              borderRadius: '6px',
                              border: '1px solid #94a3b8',
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.3rem',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease-in-out'
                            }}
                            onClick={() => window.open(order.nfe.xml_path.startsWith('http') ? order.nfe.xml_path : `${API_BASE_URL.replace('/src/php', '')}/${order.nfe.xml_path}`, '_blank')}
                            title="Acessar XML da NF-e"
                          >
                            <FileText size={13} /> XML
                          </button>
                        </>
                      )}

                      {order.status_pagamento !== 'PAGO' && (
                        <button 
                          style={{ 
                            height: '28px', 
                            padding: '0 0.55rem', 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            borderRadius: '6px',
                            border: '1px solid #fbbf24',
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.3rem',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease-in-out'
                          }}
                          onClick={() => handleEditOrder(order)}
                          title="Editar Pedido"
                        >
                          <Edit2 size={13} /> Editar
                        </button>
                      )}

                      <button 
                        style={{ 
                          height: '28px', 
                          padding: '0 0.55rem', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          borderRadius: '6px',
                          border: '1px solid #6366f1',
                          backgroundColor: '#e0e7ff',
                          color: '#4338ca',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.3rem',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease-in-out'
                        }}
                        onClick={() => handleDuplicateOrder(order)}
                        title="Duplicar Pedido"
                      >
                        <Copy size={13} /> Duplicar
                      </button>

                      <button 
                        style={{ 
                          height: '28px', 
                          padding: '0 0.55rem', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          borderRadius: '6px',
                          border: '1px solid #f87171',
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.3rem',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease-in-out'
                        }}
                        onClick={() => handleDeleteOrder(order.id)}
                        title="Excluir Pedido"
                      >
                        <Trash2 size={13} /> Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    Nenhum pedido de venda encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL NOVO/EDITAR PEDIDO OCUPANDO 80% DA TELA */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          cancelEditingProduct();
          cancelEditingQty();
          cancelEditingPrice();
        }}
        title={formData.id ? "Editar Pedido de Venda" : "Novo Pedido de Venda"}
        width="80vw"
        maxWidth="80vw"
        style={{ height: '82vh', minHeight: '600px' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', width: '100%', height: '100%', flex: 1, justifyContent: 'space-between' }}>
          
          {/* NAVEGAÇÃO POR ABAS */}
          <div className="form-tabs">
            <button
              type="button"
              className={`tab-btn ${activeTab === 'pedido' ? 'active' : ''}`}
              onClick={() => setActiveTab('pedido')}
            >
              <ShoppingCart size={15} /> 1. Dados do Pedido & Produtos
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'transporte' ? 'active' : ''}`}
              onClick={() => setActiveTab('transporte')}
            >
              <Truck size={15} /> 2. Transporte & Frete {formData.transportadora_nome && `(${formData.transportadora_nome})`}
            </button>
          </div>

          {/* ABA 1: DADOS DO PEDIDO & PRODUTOS */}
          {activeTab === 'pedido' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', flex: 1, minHeight: 0 }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.3fr 0.85fr 0.95fr 0.9fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>Cliente <span className="text-danger">*</span></label>
                  <SearchableSelect
                    options={clients}
                    value={formData.cliente_id}
                    onChange={val => {
                      handleClientChange({ target: { value: val } });
                    }}
                    placeholder="Digite para buscar cliente..."
                    getOptionValue={c => c.cliente_id || c.id}
                    getOptionLabel={c => `${c.cliente_nome}${c.transportadora_nome ? ` [Transp: ${c.transportadora_nome}]` : ''}`}
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Tag size={15} color="var(--primary-color)" /> Tabela de Preço
                  </label>
                  <select
                    className="standard-input"
                    name="tabela_id"
                    value={formData.tabela_id || ''}
                    onChange={handleTableChange}
                  >
                    <option value="">Nenhuma (Preço Normal / 0% OFF)</option>
                    {priceTables.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.nome} ({Number(t.desconto_porcentagem).toFixed(2)}% OFF)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Percent size={15} color="var(--primary-color)" /> Desconto (%)
                  </label>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="desconto"
                      value={formData.desconto}
                      onChange={handleDiscountChange}
                      placeholder="0.00"
                      className="standard-input"
                      style={{ paddingRight: '2rem', fontWeight: '600' }}
                    />
                    <span style={{ position: 'absolute', right: '0.65rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: '600', pointerEvents: 'none' }}>
                      %
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Data <span className="text-danger">*</span></label>
                  <input type="date" name="data_contabilizacao" value={formData.data_contabilizacao} onChange={handleInputChange} required className="standard-input" />
                </div>

                <div className="form-group">
                  <label>Status Pagamento</label>
                  <select name="status_pagamento" value={formData.status_pagamento} onChange={handleInputChange} className="standard-input">
                    <option value="PENDENTE">Pendente</option>
                    <option value="PAGO">Pago</option>
                  </select>
                </div>
              </div>

              {formData.desconto_porcentagem > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#15803d',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  <Percent size={15} />
                  <span>Tabela Aplicada: <strong>{formData.tabela_nome}</strong> — <strong>{Number(formData.desconto_porcentagem).toFixed(2)}% OFF</strong> aplicado automaticamente em todos os itens do pedido!</span>
                </div>
              )}

              <div className="form-section-title">
                <Package size={15} /> Itens do Pedido
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                 <div className="form-group flex-3">
                   <label>Adicionar Produto</label>
                   <SearchableSelect
                     options={products}
                     value={selectedProduct}
                     onChange={val => setSelectedProduct(val)}
                     placeholder="Digite o nome ou código do produto..."
                     getOptionValue={p => p.id}
                     getOptionLabel={p => {
                       const baseP = parseFloat(p.produto_preco || 0);
                       const descP = parseFloat(formData.desconto_porcentagem || 0);
                       const finalP = baseP * (1 - descP / 100);
                       const priceText = descP > 0 
                         ? `R$ ${finalP.toFixed(2)} (${descP}% OFF)` 
                         : `R$ ${baseP.toFixed(2)}`;
                       return `${p.produto_nome} (${priceText}) | Est: ${p.produto_quantidade}`;
                     }}
                     renderOption={p => {
                       const baseP = parseFloat(p.produto_preco || 0);
                       const descP = parseFloat(formData.desconto_porcentagem || 0);
                       const finalP = baseP * (1 - descP / 100);
                       return (
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                           <span style={{ fontWeight: '600', color: '#1e293b' }}>{p.produto_nome}</span>
                           <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#64748b' }}>
                             <span>
                               {descP > 0 ? (
                                 <>
                                   <span style={{ textDecoration: 'line-through', marginRight: '4px' }}>R$ {baseP.toFixed(2)}</span>
                                   <strong style={{ color: '#16a34a' }}>R$ {finalP.toFixed(2)} ({descP}% OFF)</strong>
                                 </>
                               ) : (
                                 <strong style={{ color: '#0f172a' }}>R$ {baseP.toFixed(2)}</strong>
                               )}
                             </span>
                             <span>• Estoque: <strong style={{ color: p.produto_quantidade > 0 ? '#334155' : '#dc2626' }}>{p.produto_quantidade}</strong></span>
                           </div>
                         </div>
                       );
                     }}
                   />
                 </div>
                 <div className="form-group flex-1" style={{ maxWidth: '80px' }}>
                   <label>Qtd.</label>
                   <input type="number" min="1" className="standard-input" value={itemQuantity} onChange={e => setItemQuantity(e.target.value)} />
                 </div>
                 <button type="button" className="btn-primary" onClick={handleAddItem} style={{ height: '38px', padding: '0 1.25rem', width: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                   Adicionar
                 </button>
              </div>

              {formData.itens.length > 0 ? (
                <div className="table-responsive" style={{ border: '1px solid var(--border-color)', borderRadius: '6px', minHeight: '200px', maxHeight: '360px', flex: 1, overflowY: 'auto' }}>
                  <table className="data-table" style={{ fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '0.35rem 0.6rem' }}>Produto</th>
                        <th style={{ padding: '0.35rem 0.6rem' }}>Qtd</th>
                        <th style={{ padding: '0.35rem 0.6rem' }}>V. Unitário</th>
                        <th style={{ padding: '0.35rem 0.6rem' }}>Subtotal</th>
                        <th style={{ padding: '0.35rem 0.6rem' }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.itens.map((item, idx) => {
                        const pOrig = Number(item.preco_original !== undefined && item.preco_original !== null ? item.preco_original : item.preco_unitario || 0);
                        const pUnit = Number(item.preco_unitario || 0);
                        const pSub = Number(item.subtotal !== undefined && item.subtotal !== null ? item.subtotal : (pUnit * (item.quantidade || 0)));
                        const hasDiscount = pOrig > 0 && pOrig > pUnit;

                        let charSummary = [];
                        if (item.caracteristicas) {
                          const chars = typeof item.caracteristicas === 'string' ? (JSON.parse(item.caracteristicas || '{}')) : item.caracteristicas;
                          if (chars.cor) charSummary.push(`Cor: ${chars.cor}`);
                          if (chars.caimento) charSummary.push(`Caimento: ${chars.caimento}`);
                          if (chars.vareta) charSummary.push(`Vareta: ${chars.vareta}`);
                          if (chars.extras && Array.isArray(chars.extras)) {
                            chars.extras.forEach(e => {
                              if (e.titulo || e.detalhe) charSummary.push(`${e.titulo ? e.titulo + ': ' : ''}${e.detalhe || ''}`);
                            });
                          }
                        }

                        return (
                          <tr key={idx}>
                            <td style={{ padding: '0.35rem 0.6rem' }}>
                              {editingProductIndex === idx ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', width: '100%', minWidth: '240px' }}>
                                  <div style={{ flex: 1, minWidth: '200px' }}>
                                    <SearchableSelect
                                      options={products}
                                      value={tempProductValue}
                                      onChange={val => {
                                        setTempProductValue(val);
                                        if (val) {
                                          saveEditingProduct(idx, val);
                                        }
                                      }}
                                      placeholder="Digite o nome ou código do produto..."
                                      getOptionValue={p => p.id}
                                      getOptionLabel={p => {
                                        const baseP = parseFloat(String(p.produto_preco || 0).replace(',', '.')) || 0;
                                        const descP = parseFloat(formData.desconto_porcentagem || 0);
                                        const finalP = baseP * (1 - descP / 100);
                                        const priceText = descP > 0 
                                          ? `R$ ${finalP.toFixed(2)} (${descP}% OFF)` 
                                          : `R$ ${baseP.toFixed(2)}`;
                                        return `${p.produto_nome} (${priceText}) | Est: ${p.produto_quantidade}`;
                                      }}
                                      renderOption={p => {
                                        const baseP = parseFloat(String(p.produto_preco || 0).replace(',', '.')) || 0;
                                        const descP = parseFloat(formData.desconto_porcentagem || 0);
                                        const finalP = baseP * (1 - descP / 100);
                                        return (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span style={{ fontWeight: '600', color: '#1e293b' }}>{p.produto_nome}</span>
                                            <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#64748b' }}>
                                              <span>
                                                {descP > 0 ? (
                                                  <>
                                                    <span style={{ textDecoration: 'line-through', marginRight: '4px' }}>R$ {baseP.toFixed(2)}</span>
                                                    <strong style={{ color: '#16a34a' }}>R$ {finalP.toFixed(2)} ({descP}% OFF)</strong>
                                                  </>
                                                ) : (
                                                  <strong style={{ color: '#0f172a' }}>R$ {baseP.toFixed(2)}</strong>
                                                )}
                                              </span>
                                              <span>• Estoque: <strong style={{ color: p.produto_quantidade > 0 ? '#334155' : '#dc2626' }}>{p.produto_quantidade}</strong></span>
                                            </div>
                                          </div>
                                        );
                                      }}
                                      usePortal={true}
                                      autoOpen={true}
                                      height="32px"
                                      fontSize="0.8rem"
                                    />
                                  </div>
                                  <button 
                                    type="button" 
                                    onClick={() => saveEditingProduct(idx)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#16a34a', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      flexShrink: 0
                                    }}
                                    title="Salvar produto alterado"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button 
                                    type="button" 
                                    onClick={cancelEditingProduct}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#dc2626', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      flexShrink: 0
                                    }}
                                    title="Cancelar"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.35rem' }}>
                                  <div>
                                    <span style={{ fontWeight: '500' }}>{item.produto_nome}</span>
                                    {charSummary.length > 0 && (
                                      <div style={{ fontSize: '0.725rem', color: '#7c3aed', fontWeight: '500', marginTop: '0.15rem' }}>
                                        {charSummary.join(' | ')}
                                      </div>
                                    )}
                                  </div>
                                  <button 
                                    type="button" 
                                    onClick={() => startEditingProduct(idx, item.produto_id)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: 'var(--text-muted)', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'inline-flex', 
                                      alignItems: 'center',
                                      borderRadius: '4px',
                                      flexShrink: 0
                                    }}
                                    title="Alterar produto selecionado"
                                  >
                                    <Pencil size={12} color="var(--primary-color)" />
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.35rem 0.6rem', whiteSpace: 'nowrap' }}>
                              {editingQtyIndex === idx ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <input 
                                    type="number" 
                                    min="1" 
                                    value={tempQtyValue} 
                                    onChange={(e) => setTempQtyValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditingQty(idx);
                                      if (e.key === 'Escape') cancelEditingQty();
                                    }}
                                    autoFocus
                                    style={{ 
                                      width: '55px', 
                                      padding: '0.15rem 0.35rem', 
                                      fontSize: '0.8rem', 
                                      borderRadius: '4px', 
                                      border: '1px solid var(--primary-color)',
                                      outline: 'none'
                                    }} 
                                  />
                                  <button 
                                    type="button" 
                                    onClick={() => saveEditingQty(idx)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#16a34a', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'flex', 
                                      alignItems: 'center' 
                                    }}
                                    title="Salvar quantidade"
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button 
                                    type="button" 
                                    onClick={cancelEditingQty}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#dc2626', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'flex', 
                                      alignItems: 'center' 
                                    }}
                                    title="Cancelar"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                  <span style={{ fontWeight: '500' }}>{item.quantidade}</span>
                                  <button 
                                    type="button" 
                                    onClick={() => startEditingQty(idx, item.quantidade)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: 'var(--text-muted)', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'inline-flex', 
                                      alignItems: 'center',
                                      borderRadius: '4px'
                                    }}
                                    title="Editar quantidade do item"
                                  >
                                    <Pencil size={12} color="var(--primary-color)" />
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.35rem 0.6rem', whiteSpace: 'nowrap' }}>
                              {editingPriceIndex === idx ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>R$</span>
                                  <input 
                                    type="number" 
                                    step="0.01"
                                    min="0" 
                                    value={tempPriceValue} 
                                    onChange={(e) => setTempPriceValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditingPrice(idx);
                                      if (e.key === 'Escape') cancelEditingPrice();
                                    }}
                                    autoFocus
                                    style={{ 
                                      width: '75px', 
                                      padding: '0.15rem 0.35rem', 
                                      fontSize: '0.8rem', 
                                      borderRadius: '4px', 
                                      border: '1px solid var(--primary-color)',
                                      outline: 'none'
                                    }} 
                                  />
                                  <button 
                                    type="button" 
                                    onClick={() => saveEditingPrice(idx)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#16a34a', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'flex', 
                                      alignItems: 'center' 
                                    }}
                                    title="Salvar valor unitário e atualizar no estoque"
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button 
                                    type="button" 
                                    onClick={cancelEditingPrice}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: '#dc2626', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'flex', 
                                      alignItems: 'center' 
                                    }}
                                    title="Cancelar"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                  {hasDiscount ? (
                                    <span>
                                      <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: '0.75rem', marginRight: '0.3rem' }}>
                                        R$ {pOrig.toFixed(2)}
                                      </span>
                                      <strong style={{ color: 'var(--success-color)' }}>
                                        R$ {pUnit.toFixed(2)}
                                      </strong>
                                    </span>
                                  ) : (
                                    <span>R$ {pUnit.toFixed(2)}</span>
                                  )}
                                  <button 
                                    type="button" 
                                    onClick={() => startEditingPrice(idx, pUnit)}
                                    style={{ 
                                      background: 'none', 
                                      border: 'none', 
                                      color: 'var(--text-muted)', 
                                      cursor: 'pointer', 
                                      padding: '2px', 
                                      display: 'inline-flex', 
                                      alignItems: 'center',
                                      borderRadius: '4px'
                                    }}
                                    title="Editar valor unitário do item (atualiza no estoque também)"
                                  >
                                    <Pencil size={12} color="var(--primary-color)" />
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.35rem 0.6rem', fontWeight: '600' }}>R$ {pSub.toFixed(2)}</td>
                            <td style={{ padding: '0.35rem 0.6rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                <button 
                                  type="button" 
                                  onClick={() => openCharModal(idx)} 
                                  style={{ 
                                    height: '26px', 
                                    padding: '0 0.5rem', 
                                    fontSize: '0.725rem', 
                                    fontWeight: '600',
                                    borderRadius: '4px', 
                                    border: '1px solid #c084fc', 
                                    backgroundColor: '#f3e8ff', 
                                    color: '#7c3aed', 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: '0.25rem', 
                                    cursor: 'pointer' 
                                  }} 
                                  title="Adicionar / Editar Características do Produto"
                                >
                                  <Sliders size={12} /> Características
                                </button>
                                <button type="button" className="btn-icon danger" style={{ width: '24px', height: '24px' }} onClick={() => handleRemoveItem(idx)}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="form-hint" style={{ color: 'var(--warning-color)', fontSize: '0.75rem' }}>
                  Nenhum produto adicionado ao pedido ainda.
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="form-group flex-1">
                  <label>Observações do Pedido</label>
                  <input type="text" name="observacoes" value={formData.observacoes} onChange={handleInputChange} placeholder="Instrução ou observação interna..." className="standard-input" />
                </div>
                <div style={{ textAlign: 'right', minWidth: '190px' }}>
                  {Number(String(formData.desconto || 0).replace(',', '.')) > 0 && formData.itens.length > 0 && (() => {
                    const subItens = formData.itens.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
                    const descP = parseFloat(String(formData.desconto || 0).replace(',', '.')) || 0;
                    const descReais = subItens * (descP / 100);
                    return (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>
                        <span>Subtotal: <span style={{ textDecoration: 'line-through' }}>R$ {subItens.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                        <span style={{ color: '#16a34a', fontWeight: '600', marginLeft: '0.45rem' }}>
                          (-{descP.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}% / -R$ {descReais.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </span>
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Valor Total</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--primary-color)' }}>
                    R$ {Number(formData.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
                <button type="button" className="btn-outline" style={{ padding: '0.4rem 0.85rem', fontSize: '0.8rem' }} onClick={() => setActiveTab('transporte')}>
                  Avançar para Transporte & Frete →
                </button>
              </div>
            </div>
          )}

          {/* ABA 2: DADOS DE TRANSPORTE & FRETE */}
          {activeTab === 'transporte' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1, minHeight: 0 }}>
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group flex-2">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Truck size={15} color="var(--primary-color)" /> Transportadora
                  </label>
                  <select
                    className="standard-input"
                    name="transportadora_id"
                    value={formData.transportadora_id}
                    onChange={handleCarrierSelectChange}
                  >
                    <option value="">Nenhuma Transportadora / Frete Próprio</option>
                    {carriers.map(tr => (
                      <option key={tr.id} value={tr.id}>
                        {tr.nome} {tr.cnpj_cpf ? `(CNPJ: ${tr.cnpj_cpf})` : ''} {tr.cidade ? `- ${tr.cidade}/${tr.uf}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group flex-2">
                  <label>Modalidade do Frete (modFrete) <span className="text-danger">*</span></label>
                  <select
                    className="standard-input"
                    name="mod_frete"
                    value={formData.mod_frete}
                    onChange={handleInputChange}
                  >
                    <option value={0}>0 - Frete por conta do Remetente (CIF)</option>
                    <option value={1}>1 - Frete por conta do Destinatário (FOB)</option>
                    <option value={2}>2 - Frete por conta de Terceiros</option>
                    <option value={3}>3 - Transp. Próprio Remetente</option>
                    <option value={4}>4 - Transp. Próprio Destinatário</option>
                    <option value={9}>9 - Sem Ocorrência de Transporte (Sem Frete)</option>
                  </select>
                </div>
              </div>

              <div className="form-section-title">
                <Truck size={15} /> Veículo de Carga & Volumes
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group flex-1">
                  <label>Placa do Veículo</label>
                  <input
                    type="text"
                    name="placa_veiculo"
                    value={formData.placa_veiculo}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="ABC1D23"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="form-group" style={{ width: '80px' }}>
                  <label>UF Placa</label>
                  <input
                    type="text"
                    name="uf_veiculo"
                    value={formData.uf_veiculo}
                    onChange={handleInputChange}
                    className="standard-input"
                    maxLength="2"
                    style={{ textTransform: 'uppercase' }}
                    placeholder="SP"
                  />
                </div>
                <div className="form-group flex-1">
                  <label>RNTRC</label>
                  <input
                    type="text"
                    name="rntrc"
                    value={formData.rntrc}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="Cód. RNTRC"
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Qtd. Volumes</label>
                  <input
                    type="number"
                    min="0"
                    name="q_vol"
                    value={formData.q_vol}
                    onChange={handleInputChange}
                    className="standard-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group flex-1">
                  <label>Espécie</label>
                  <input
                    type="text"
                    name="esp"
                    value={formData.esp}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="CAIXA, PALLETE..."
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Marca</label>
                  <input
                    type="text"
                    name="marca"
                    value={formData.marca}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="Marca das caixas"
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Peso Bruto (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    name="peso_b"
                    value={formData.peso_b}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="0.000"
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Peso Líquido (kg)</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    name="peso_l"
                    value={formData.peso_l}
                    onChange={handleInputChange}
                    className="standard-input"
                    placeholder="0.000"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '0.25rem' }}>
                <button type="button" className="btn-outline" style={{ padding: '0.4rem 0.85rem', fontSize: '0.8rem' }} onClick={() => setActiveTab('pedido')}>
                  ← Voltar para Produtos
                </button>
              </div>

            </div>
          )}

          {/* BOTÕES DE AÇÃO INFERIORES */}
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            {formData.id && (
              <button 
                type="button" 
                className="btn-outline" 
                style={{ borderColor: '#7c3aed', color: '#7c3aed', padding: '0.55rem 1rem' }}
                onClick={() => window.open(`${API_BASE_URL}/orcamento_pdf.php?venda_id=${formData.id}`, '_blank')}
              >
                <Printer size={15} style={{ marginRight: '0.3rem' }} /> Gerar Orçamento PDF
              </button>
            )}
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="button" className="btn-success" onClick={handleSubmit} style={{ width: 'auto', padding: '0.55rem 1.5rem' }}>
              Salvar Pedido de Venda
            </button>
          </div>

        </div>
      </Modal>

      {/* MODAL PARA ADICIONAR / EDITAR CARACTERÍSTICAS DO PRODUTO */}
      <Modal 
        isOpen={isCharModalOpen} 
        onClose={() => setIsCharModalOpen(false)}
        title={currentCharItemIndex !== null && formData.itens[currentCharItemIndex] 
          ? `Características: ${formData.itens[currentCharItemIndex].produto_nome}` 
          : "Adicionar Características"}
        maxWidth="620px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          
          <div className="form-section-title" style={{ marginTop: 0 }}>
            <Sliders size={16} /> Características Primárias
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Cor</label>
              <input 
                type="text" 
                name="cor" 
                value={charFormData.cor} 
                onChange={handleCharInputChange} 
                placeholder="Ex: Azul, Preto..." 
                className="standard-input" 
              />
            </div>
            <div className="form-group">
              <label>Caimento</label>
              <input 
                type="text" 
                name="caimento" 
                value={charFormData.caimento} 
                onChange={handleCharInputChange} 
                placeholder="Ex: Ajustado, Solto..." 
                className="standard-input" 
              />
            </div>
            <div className="form-group">
              <label>Vareta</label>
              <input 
                type="text" 
                name="vareta" 
                value={charFormData.vareta} 
                onChange={handleCharInputChange} 
                placeholder="Ex: Fibra, Alumínio..." 
                className="standard-input" 
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div className="form-section-title" style={{ margin: 0 }}>
                <Plus size={16} /> Características Adicionais
              </div>
              <button 
                type="button" 
                onClick={handleAddExtraChar}
                style={{
                  height: '28px',
                  padding: '0 0.75rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: '1px solid var(--primary-color)',
                  backgroundColor: 'var(--primary-light)',
                  color: 'var(--primary-color)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  cursor: 'pointer'
                }}
              >
                <Plus size={14} /> Adicionar característica
              </button>
            </div>

            {charFormData.extras.length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', margin: '0.5rem 0' }}>
                Nenhuma característica personalizada adicionada ainda.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '180px', overflowY: 'auto', paddingRight: '0.25rem' }}>
                {charFormData.extras.map((extra, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input 
                      type="text" 
                      placeholder="Título (ex: Tamanho)" 
                      value={extra.titulo} 
                      onChange={(e) => handleExtraCharChange(index, 'titulo', e.target.value)}
                      className="standard-input" 
                      style={{ flex: 1 }}
                    />
                    <input 
                      type="text" 
                      placeholder="Detalhe (ex: G, 100cm)" 
                      value={extra.detalhe} 
                      onChange={(e) => handleExtraCharChange(index, 'detalhe', e.target.value)}
                      className="standard-input" 
                      style={{ flex: 1.5 }}
                    />
                    <button 
                      type="button" 
                      className="btn-icon danger" 
                      style={{ width: '32px', height: '32px', flexShrink: 0 }}
                      onClick={() => handleRemoveExtraChar(index)}
                      title="Remover característica"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={() => setIsCharModalOpen(false)}>
              Cancelar
            </button>
            <button type="button" className="btn-primary" style={{ width: 'auto', padding: '0.45rem 1.25rem' }} onClick={handleSaveCharacteristics}>
              Salvar Características
            </button>
          </div>

        </div>
      </Modal>
    </div>
  );
}
